import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import { useSitemap, useLabels, useRequestData } from 'wsm-common-data';
import { getDestinationLink, getClickHandler } from '../utilities/links';
import EventPromo from './EventPromo';
import PartsPromo from './PartsPromo';
import ServicePromo from './ServicePromo';
import VehiclePromo from './VehiclePromo';
import IncentivePromo from './IncentivePromo';
import Asset from './Asset';
import { getDiscountDisplay } from '../utilities/general';
import { getCouponStatus } from '../utilities/aspect';
import { g } from '../global-constants';
import { useURLPrefs } from '../hooks/useURLPrefs';

const Promo = ({
	aspectRatio,
	carouselPosition,
	isMobile = false,
	isVisible = true,
	loadPromo = true,
	positionLabel = null,
	promo,
	role = null,
	roleDescription = null,
	width,
	onPromoPushOutsideButton
}) => {
	const {
		autoGenerated = false,
		condition = 'NEW',
		couponCode = '',
		customAssets = {},
		description = '',
		destinationLabel = '',
		destinationUrl = '',
		discountType = '',
		discountValue = null,
		globalVehicleId = null,
		id,
		imageUrl = null,
		incentiveId = null,
		inventoryCount = 0,
		title = '',
		type,
		vehicleImages = [],
		oemId = null
	} = promo;

	const labels = useLabels();
	const { enableCarousel, collection } = useURLPrefs();
	const sitemap = useSitemap();
	const { widgetName, windowId, locale } = useRequestData();
	const personalized = useSelector((state) => state.personalized);
	const isCoupon = getCouponStatus(aspectRatio);
	const isCouponMobile = isCoupon && isMobile;
	const isCustomAsset = customAssets && customAssets[aspectRatio];
	const classes = [
		'promo',
		`promo-type-${type.toLowerCase()}`,
		`collection-${collection.toLowerCase()}`,
		isCustomAsset ? 'custom-asset' : 'non-custom-asset',
		isCouponMobile && enableCarousel === 'false' ? 'mb-4' : ''
	];

	// Handle destination link defaults
	const {
		url: ctaUrl,
		label: ctaLabel,
		isFullSlideLink
	} = getDestinationLink({
		condition,
		label: destinationLabel,
		url: destinationUrl,
		inventoryCount,
		isCustomAsset,
		labels,
		sitemap,
		type
	});
	// Create Discount display labels for FO promos
	const discountDisplay = getDiscountDisplay(
		locale,
		discountType,
		discountValue,
		labels
	);

	let promoContent = <div className="hidden">Placeholder</div>;
	let addFullSlideCta = isFullSlideLink;
	const [loadContent, setLoadContent] = useState(false);
	const incentives = useSelector((state) => state.incentives);
	const shouldShowIncentive =
		incentiveId &&
		globalVehicleId &&
		incentives &&
		incentives.length &&
		type === g.INCENTIVE;

	useEffect(() => {
		// Module refresh required to pick up new '.dialog' elements for
		// jQuery dialog/slide-in functionality
		if (loadContent) {
			if (isMobile) {
				window.DDC.mobile.slidein.init();
			} else if (typeof $ === 'function') {
				$(document).trigger('modulesRefresh', {
					modules: {
						dialog: window.DDC.modules.dialog
					}
				});
			}
		}
	}, [isMobile, loadContent]);

	useEffect(() => {
		// Load slide content/assets and store "loaded" state (i.e. never "unload")
		if (loadPromo) {
			setLoadContent(true);
		}
	}, [loadPromo]);

	if (loadPromo || loadContent) {
		if (isCustomAsset) {
			// Render custom asset
			classes.push('slide-type-image');
			addFullSlideCta = !!ctaUrl;
			if (shouldShowIncentive) {
				const promoIncentives = incentives.filter(
					(incentive) =>
						!incentive.error &&
						incentiveId === incentive.incentiveId
				);
				if (promoIncentives.length) {
					const [promoIncentive] = promoIncentives;
					promoContent = (
						<Asset
							carouselPosition={carouselPosition}
							id={id}
							imageUrl={customAssets[aspectRatio]}
							isMobile={isMobile}
							isVisible={isVisible}
							promo={promo}
							title={title}
							type={type}
							width={width}
							pushOutsiderButton={(btn) =>
								onPromoPushOutsideButton(btn)
							}
							incentive={promoIncentive}
						/>
					);
				}
			} else {
				promoContent = (
					<Asset
						carouselPosition={carouselPosition}
						id={id}
						imageUrl={customAssets[aspectRatio]}
						isMobile={isMobile}
						isVisible={isVisible}
						promo={promo}
						title={title}
						type={type}
						width={width}
						pushOutsiderButton={(btn) =>
							onPromoPushOutsideButton(btn)
						}
					/>
				);
			}
		} else {
			// Render HTML content
			classes.push('slide-type-html');

			switch (type) {
				case g.EVENT:
					promoContent = (
						<EventPromo
							aspectRatio={aspectRatio}
							carouselPosition={carouselPosition}
							ctaLabel={ctaLabel}
							ctaUrl={ctaUrl}
							description={description}
							id={id}
							imageUrl={imageUrl}
							isMobile={isMobile}
							isVisible={isVisible}
							promo={promo}
							title={title}
							type={type}
							width={width}
						/>
					);
					break;

				case g.PARTS:
					promoContent = (
						<PartsPromo
							aspectRatio={aspectRatio}
							carouselPosition={carouselPosition}
							couponCode={couponCode}
							ctaLabel={ctaLabel}
							ctaUrl={ctaUrl}
							description={description}
							discountDisplay={discountDisplay}
							id={id}
							imageUrl={imageUrl}
							isMobile={isMobile}
							isVisible={isVisible}
							promo={promo}
							title={title}
							type={type}
							width={width}
						/>
					);
					break;

				case g.SERVICE:
					promoContent = (
						<ServicePromo
							aspectRatio={aspectRatio}
							carouselPosition={carouselPosition}
							couponCode={couponCode}
							ctaLabel={ctaLabel}
							ctaUrl={ctaUrl}
							description={description}
							discountDisplay={discountDisplay}
							id={id}
							imageUrl={imageUrl}
							isMobile={isMobile}
							isVisible={isVisible}
							promo={promo}
							title={title}
							type={type}
							width={width}
						/>
					);
					break;

				case g.INCENTIVE:
					if (shouldShowIncentive) {
						// Add the matching incentive data
						const promoIncentive = incentives.filter(
							(incentive) =>
								!incentive.error &&
								incentiveId === incentive.incentiveId
						);

						if (promoIncentive.length) {
							const incentive = promoIncentive[0];

							promoContent = (
								<IncentivePromo
									aspectRatio={aspectRatio}
									carouselPosition={carouselPosition}
									ctaLabel={ctaLabel}
									ctaUrl={ctaUrl}
									globalVehicleId={globalVehicleId}
									id={id}
									imageUrl={imageUrl}
									incentive={incentive}
									isMobile={isMobile}
									isVisible={isVisible}
									promo={promo}
									type={type}
									width={width}
								/>
							);
						}
					}
					break;

				case g.VEHICLE:
					promoContent = (
						<VehiclePromo
							aspectRatio={aspectRatio}
							carouselPosition={carouselPosition}
							ctaLabel={ctaLabel}
							ctaUrl={ctaUrl}
							description={description}
							id={id}
							imageUrl={imageUrl}
							isMobile={isMobile}
							isVisible={isVisible}
							promo={promo}
							title={title}
							type={type}
							vehicleImages={vehicleImages}
							width={width}
						/>
					);
					break;

				default:
					promoContent = null;
			}
		}
	}

	if (promoContent) {
		const promoType = isCustomAsset ? 'Asset' : type;
		const clickHandler = getClickHandler(promoType, {
			widgetName,
			windowId,
			promo,
			aspectRatio,
			personalized,
			carouselPosition
		});
		// Wrap with anchor if the full slide is a link
		// Set tab index when slide is not being displayed so that keyboard users can't access it
		const wrappedContent = addFullSlideCta ? (
			<a
				href={ctaUrl}
				aria-label={ctaLabel || title}
				target={promo?.ctaConfig?.openInNewTab ? '_blank' : '_self'}
				tabIndex={!isVisible ? '-1' : null}
				onClick={clickHandler} rel="noreferrer"
			>
				{promoContent}
			</a>
		) : (
			promoContent
		);

		// TODO: need to figure out whether this promo was optimized with EO
		// data-promo-eo={true}
		// window.DDC.siteSettings.isEo <- Checks for the DNA resource
		return (
			<div
				className={setClassNames(classes)}
				data-promo-id={id}
				data-oem-id={oemId}
				// data-promo-eo={false}
				data-auto-generated={autoGenerated}
				data-url-href={addFullSlideCta ? ctaUrl : null}
				role={role}
				aria-current={isVisible ? 'true' : 'false'}
				aria-roledescription={roleDescription}
				aria-label={positionLabel}
			>
				{wrappedContent}
			</div>
		);
	}

	return null;
};

Promo.propTypes = {
	aspectRatio: PropTypes.string.isRequired,
	carouselPosition: PropTypes.number.isRequired,
	isMobile: PropTypes.bool,
	isVisible: PropTypes.bool,
	loadPromo: PropTypes.bool,
	positionLabel: PropTypes.string,
	promo: PropTypes.shape({
		additionalDetails: PropTypes.string,
		autoGenerated: PropTypes.bool,
		condition: PropTypes.string,
		couponCode: PropTypes.string,
		customAssets: PropTypes.shape(),
		description: PropTypes.string,
		destinationLabel: PropTypes.string,
		destinationUrl: PropTypes.string,
		disclaimer: PropTypes.string,
		discountType: PropTypes.string,
		discountValue: PropTypes.number,
		globalVehicleId: PropTypes.string,
		id: PropTypes.string.isRequired,
		imageUrl: PropTypes.string,
		incentiveId: PropTypes.string,
		inventoryCount: PropTypes.number,
		title: PropTypes.string,
		type: PropTypes.string.isRequired,
		vehicleImages: PropTypes.arrayOf(PropTypes.string),
		oemId: PropTypes.string,
		ctaConfig: PropTypes.shape({
			openInNewTab: PropTypes.bool
		})
	}).isRequired,
	role: PropTypes.string,
	onPromoPushOutsideButton: PropTypes.func.isRequired,
	roleDescription: PropTypes.string,
	width: PropTypes.number.isRequired
};

export default Promo;
